import { useState, type JSX } from 'react';
import { MAX_PLAY_NAME } from '../core/index.ts';
import type { SavedPlaysApi } from './useSavedPlays.ts';

interface SavedPlaysProps {
  readonly plays: SavedPlaysApi;
}

/**
 * The coach's saved plays (#92), under Record: save the board as a play once
 * it is right, open one again, start a new one, delete one.
 *
 * Save updates the play the board came from; Save as… — and Save on a board
 * no play is behind — asks for a name. A name another play already has asks
 * before replacing it. Opening a play replaces the board without asking: one
 * Undo brings back what was there.
 */
export function SavedPlays({ plays }: SavedPlaysProps): JSX.Element {
  // The name being typed, or null while the form is shut.
  const [naming, setNaming] = useState<string | null>(null);
  // Set once the coach was asked whether to replace the play of that name.
  const [askingReplace, setAskingReplace] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const { current, unsaved } = plays;
  const clash = naming !== null ? plays.named(naming) : undefined;
  // Saving under the open play's own name is just Save: nothing to ask.
  const replaces = clash && clash.id !== current?.id ? clash : undefined;

  const closeForm = (): void => {
    setNaming(null);
    setAskingReplace(false);
  };

  const finishNaming = async (): Promise<void> => {
    if (naming === null || !naming.trim()) return;
    if (replaces && !askingReplace) {
      setAskingReplace(true);
      return;
    }
    if (await plays.saveAs(naming)) closeForm();
  };

  return (
    <div className="saved-plays" data-tutorial="saved-plays">
      <h2 className="toolbar__heading">Saved plays</h2>

      <div className="toolbar__row">
        <button
          type="button"
          className={unsaved ? 'btn btn--primary' : 'btn'}
          disabled={current !== undefined && !unsaved}
          onClick={() => {
            if (current) void plays.save();
            else setNaming('');
          }}
        >
          {current && !unsaved ? 'Saved' : 'Save'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setNaming('')}>
          Save as…
        </button>
        <button type="button" className="btn btn--ghost" onClick={plays.newPlay}>
          New play
        </button>
      </div>

      {naming !== null && (
        <form
          className="formation-save"
          onSubmit={(event) => {
            event.preventDefault();
            void finishNaming();
          }}
        >
          <input
            type="text"
            aria-label="Play name"
            placeholder="Name this play"
            maxLength={MAX_PLAY_NAME}
            value={naming}
            autoFocus
            onChange={(event) => {
              setNaming(event.target.value);
              setAskingReplace(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeForm();
            }}
          />
          {askingReplace && replaces ? (
            <>
              <p className="toolbar__hint" role="alert">
                There’s already a play called “{replaces.name}”. Replace it?
              </p>
              <div className="toolbar__row">
                <button type="submit" className="btn btn--danger">
                  Replace
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => setAskingReplace(false)}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="toolbar__row">
              <button type="submit" className="btn" disabled={!naming.trim()}>
                Save
              </button>
              <button type="button" className="btn btn--ghost" onClick={closeForm}>
                Cancel
              </button>
            </div>
          )}
        </form>
      )}

      {plays.saved.length === 0 ? (
        <p className="toolbar__hint">Happy with a play? Save it under a name and open it again any time.</p>
      ) : (
        <ul className="saved-plays__list">
          {plays.saved.map((play) => {
            const open = play.id === current?.id;
            return (
              <li key={play.id} className={open ? 'saved-plays__item saved-plays__item--open' : 'saved-plays__item'}>
                <button
                  type="button"
                  className="saved-plays__open"
                  aria-current={open ? 'true' : undefined}
                  title={open ? `${play.name} is on the board` : `Open ${play.name}`}
                  onClick={() => plays.open(play.id)}
                >
                  <span className="saved-plays__name">{play.name}</span>
                  {open && unsaved && <span className="saved-plays__unsaved">• unsaved changes</span>}
                </button>
                {confirmingDelete === play.id ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => {
                        setConfirmingDelete(null);
                        void plays.remove(play.id);
                      }}
                    >
                      Delete?
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={() => setConfirmingDelete(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    aria-label={`Delete ${play.name}`}
                    title={`Delete ${play.name}`}
                    onClick={() => setConfirmingDelete(play.id)}
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {plays.unreadable > 0 && (
        <p className="toolbar__hint">
          {plays.unreadable} saved with a newer version of the app {plays.unreadable === 1 ? 'is' : 'are'} kept but not
          shown here.
        </p>
      )}
      {plays.error && <p className="toolbar__hint toolbar__hint--error">{plays.error}</p>}
    </div>
  );
}
